import React, { useCallback, useEffect, useRef } from "react";
import type { UIMessage } from "ai";
import styled from "styled-components";
import { AssistantContent } from "./AssistantContent";
import { Loader } from "./Loader";
import type { ToolCategory } from "./WidgetApproval";

const AiAvatarBadge = styled.div`
  background: #4d54d1;
`;

export interface MessagesProps {
  messages: Array<UIMessage>;
  userEmail: string;
  propertyTicker: string;
  status?: "submitted" | "streaming" | "ready" | "error";
  onApproval: (id: string, approved: boolean) => void;
  /**
   * Ids of messages hydrated from stored history (a previous session). A never-answered
   * approval in one of these persists as `approval-requested`; we mark it historical so its
   * widget renders a neutral note instead of actionable Yes/No buttons for a stale turn.
   */
  historicalIds?: Set<string>;
}

export function Messages({
  messages,
  userEmail,
  propertyTicker,
  status,
  onApproval,
  historicalIds,
}: MessagesProps): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  // DOM node per message id — used to find the last rendered message's element so it can be
  // anchored to the top of the viewport.
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  // Confirming/cancelling an approval widget doesn't add a new message — the agent's
  // feedback (tool result, follow-up text) is appended *inside* the same last assistant
  // message that was already anchored to the top. Re-anchoring that same message's top again
  // is a no-op, so the feedback growing below it goes unseen unless we force a jump to the
  // bottom instead. We only want that for CREATE actions, though — creating something tends
  // to add a sizeable new card the user should see land at the bottom, while an UPDATE (or
  // any other category: delete/activate/deactivate/bulk) reads better re-anchored at the top
  // of that same response, same as a normal reply. Set right before calling `onApproval`,
  // this flag stays true across every follow-up update the create approval triggers (tool
  // execution, more assistant text) — not just the first — until a genuinely new user message
  // shows up, at which point it's released and the normal top-anchor behaviour resumes.
  const forceScrollToBottomRef = useRef(false);
  const approvalTurnUserIdRef = useRef<string | undefined>(undefined);
  // Answering an approval widget doesn't start a new assistant message — the SDK reuses the
  // same last assistant message (the one holding the approval's tool part) as the target for
  // the follow-up request. That message already has "visible" content (the approval card
  // itself), so the naive "does the last assistant message have visible content" check below
  // would think the reply is already in and hide the thinking dots instantly, even though the
  // agent hasn't produced anything for this new turn yet. Snapshotting the part count right
  // before the request fires lets us only count parts appended *after* that point as new.
  const approvalBaselineRef = useRef<{ id: string; count: number } | undefined>(undefined);

  const findLastUserId = useCallback((msgs: Array<UIMessage>): string | undefined => {
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].role === "user") return msgs[i].id;
    }
    return undefined;
  }, []);

  const handleApproval = useCallback(
    (id: string, approved: boolean, category: ToolCategory) => {
      if (category === "create") {
        forceScrollToBottomRef.current = true;
        approvalTurnUserIdRef.current = findLastUserId(messages);
      } else {
        forceScrollToBottomRef.current = false;
      }
      const lastMessage = messages[messages.length - 1];
      approvalBaselineRef.current =
        lastMessage?.role === "assistant"
          ? { id: lastMessage.id, count: lastMessage.parts.length }
          : undefined;
      onApproval(id, approved);
    },
    [onApproval, messages, findLastUserId]
  );

  const lastMsg = messages[messages.length - 1];
  const approvalBaseline = approvalBaselineRef.current;
  const partsToCheck =
    lastMsg?.role === "assistant" && approvalBaseline?.id === lastMsg.id
      ? lastMsg.parts.slice(approvalBaseline.count)
      : (lastMsg?.role === "assistant" && lastMsg.parts) || [];
  const lastHasVisibleContent = partsToCheck.some(p => {
    const type = (p as { type: string }).type;
    if (type === "text") return (p as { type: "text"; text: string }).text.length > 0;
    return type.startsWith("tool-");
  });
  const showLoader = status === "submitted" || (status === "streaming" && !lastHasVisibleContent);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // The "..." typing indicator renders as the last thing in the container, below whatever
    // message came before it. Anchoring that previous message to the top can leave the
    // indicator only partly visible (or cut off) below the fold. While it's showing, always
    // jump to the very bottom instead, so it's fully in view. `showLoader` is in the
    // dependency list so this also re-runs the moment the indicator appears/disappears, even
    // on renders where `messages` itself didn't change.
    if (showLoader) {
      container.scrollTop = container.scrollHeight;
      return;
    }

    // An approval was just answered and we're still in that same user turn: keep jumping to
    // the bottom so the agent's feedback (which appends to the same already-anchored message,
    // not a new one) stays visible instead of growing out of view below the fold.
    if (forceScrollToBottomRef.current) {
      if (findLastUserId(messages) === approvalTurnUserIdRef.current) {
        container.scrollTop = container.scrollHeight;
        return;
      }
      // A genuinely new user message arrived — the approval's turn is over. Release the
      // override and fall through to the normal anchoring logic below for this new turn.
      forceScrollToBottomRef.current = false;
    }

    // Otherwise, anchor the TOP of the last rendered message to the top of the viewport,
    // whatever it is: the user's own message right after they send it, or the assistant's
    // reply as it streams in.
    const lastMessage = messages[messages.length - 1];
    const el = lastMessage ? messageRefs.current.get(lastMessage.id) : undefined;
    if (el) {
      el.scrollIntoView({ block: "start" });
    } else {
      // Nothing rendered for the last message yet: fall back to the bottom of the chat.
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, showLoader, findLastUserId]);

  return (
    <div ref={containerRef} className="flex-1 min-h-0 overflow-y-auto -mx-6">
      {messages.map(({ id, role, parts }) => {
        const hasContent = parts.some(p => {
          const type = (p as { type: string }).type;
          if (type === "text") return (p as { type: "text"; text: string }).text.length > 0;
          return type.startsWith("tool-");
        });
        if (!hasContent) return null;

        const historical = historicalIds?.has(id) ?? false;

        return (
          <div
            key={id}
            ref={el => {
              if (el) messageRefs.current.set(id, el);
              else messageRefs.current.delete(id);
            }}
            className={`py-3 ${role === "assistant" ? "bg-[#F1F5FF]" : "bg-transparent"}`}>
            <div className="flex items-start gap-2 px-4">
              {role === "assistant" ? (
                <AiAvatarBadge className="w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-semibold text-white flex-shrink-0">
                  AI
                </AiAvatarBadge>
              ) : (
                <div className="w-6 h-6 rounded-lg bg-gray-300 flex items-center justify-center text-[10px] font-semibold text-[#4a4f63] flex-shrink-0">
                  {userEmail.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                {role === "assistant" ? (
                  <AssistantContent
                    parts={parts}
                    propertyTicker={propertyTicker}
                    onApproval={handleApproval}
                    historical={historical}
                  />
                ) : (
                  <span className="text-sm">
                    {parts
                      .map(p =>
                        p.type === "text" ? (p as { type: "text"; text: string }).text : ""
                      )
                      .join("")}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
      {showLoader && <Loader />}
    </div>
  );
}
